<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import ReportHeader from './ReportHeader.vue';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppCampaignReportDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignReportDialog.vue';

const { locale, t, te } = useI18n();
const campaigns = ref([]);
const selected = ref(null);
const dialog = ref(null);
const page = ref(1);
const total = ref(0);
const isLoading = ref(false);
const hasError = ref(false);
const sinceDate = ref('');
const untilDate = ref('');
const appliedSinceDate = ref('');
const appliedUntilDate = ref('');
const hasAppliedDates = computed(
  () => Boolean(appliedSinceDate.value) || Boolean(appliedUntilDate.value)
);
const metrics = [
  'enviados',
  'entregados',
  'leidos',
  'fallidos',
  'respondio',
  'no_enviado',
];
const perPage = 50;
const pageCount = computed(() => Math.max(1, Math.ceil(total.value / perPage)));
const generatedAt = computed(() => campaigns.value[0]?.generated_at);
const formatNumber = value =>
  value == null ? '—' : new Intl.NumberFormat(locale.value).format(value);
const formatUpdatedAt = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale.value, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};
const statusLabel = campaign => {
  const status = campaign.execution?.effective_status;
  if (!status) return '—';
  const key = `REPORT.CAMPAIGN_PERFORMANCE.EFFECTIVE_STATUS.${status}`;
  return te(key) ? t(key) : status;
};
const statusClass = campaign => {
  const status = campaign.execution?.effective_status;
  if (status === 'completed') return 'bg-n-teal-3 text-n-teal-11';
  if (['preflight_failed', 'permanent_failure'].includes(status))
    return 'bg-n-ruby-2 text-n-ruby-11';
  if (
    ['scheduler_not_picked', 'queue_backlog', 'processing_stalled'].includes(
      status
    )
  )
    return 'bg-n-amber-2 text-n-amber-11';
  return 'bg-n-alpha-black2 text-n-slate-11';
};
let requestId = 0;
const fetchCampaigns = async () => {
  requestId += 1;
  const current = requestId;
  isLoading.value = true;
  hasError.value = false;
  try {
    const request = {
      page: page.value,
      perPage,
    };
    if (appliedSinceDate.value) request.sinceDate = appliedSinceDate.value;
    if (appliedUntilDate.value) request.untilDate = appliedUntilDate.value;
    const { data } = await ReportsAPI.getCampaignPerformance(request);
    if (current !== requestId) return;
    campaigns.value = data.payload || data;
    total.value = data.meta?.total ?? campaigns.value.length;
  } catch (error) {
    if (current === requestId) hasError.value = true;
  } finally {
    if (current === requestId) isLoading.value = false;
  }
};
const applyDates = () => {
  appliedSinceDate.value = sinceDate.value;
  appliedUntilDate.value = untilDate.value;
  page.value = 1;
  fetchCampaigns();
};
const clearDates = () => {
  sinceDate.value = '';
  untilDate.value = '';
  applyDates();
};
const changePage = delta => {
  const nextPage = page.value + delta;
  if (nextPage < 1 || nextPage > pageCount.value || isLoading.value) return;
  page.value = nextPage;
  fetchCampaigns();
};
const openDetail = async campaign => {
  selected.value = campaign;
  await nextTick();
  dialog.value.open();
};
onMounted(fetchCampaigns);
</script>

<template>
  <div class="flex flex-col flex-1 overflow-auto pb-6">
    <ReportHeader
      :header-title="t('REPORT.CAMPAIGN_PERFORMANCE.TITLE')"
      :header-description="t('REPORT.CAMPAIGN_PERFORMANCE.DESCRIPTION')"
    >
      <Button
        :label="t('REPORT.CAMPAIGN_PERFORMANCE.REFRESH')"
        icon="i-lucide-refresh-cw"
        variant="outline"
        color="slate"
        :disabled="isLoading"
        @click="fetchCampaigns"
      />
    </ReportHeader>
    <form
      class="flex flex-wrap items-end gap-3 p-4 mb-4 rounded-xl border border-n-weak bg-n-solid-1"
      @submit.prevent="applyDates"
    >
      <label
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.SINCE_DATE') }}</span>
        <input
          v-model="sinceDate"
          type="date"
          :max="untilDate || undefined"
          :aria-label="t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.SINCE_DATE')"
          class="h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm font-normal text-n-slate-12"
        />
      </label>
      <label
        class="flex flex-col gap-1.5 mb-0 text-xs font-medium text-n-slate-11"
      >
        <span>{{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.UNTIL_DATE') }}</span>
        <input
          v-model="untilDate"
          type="date"
          :min="sinceDate || undefined"
          :aria-label="t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.UNTIL_DATE')"
          class="h-9 min-w-0 mb-0 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm font-normal text-n-slate-12"
        />
      </label>
      <Button
        type="submit"
        :label="t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.APPLY')"
      />
      <button
        data-testid="clear-campaign-dates"
        type="button"
        class="h-9 px-3 rounded-lg border border-n-weak text-sm text-n-slate-12 disabled:opacity-50"
        :disabled="!hasAppliedDates && !sinceDate && !untilDate"
        @click="clearDates"
      >
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.CLEAR') }}
      </button>
    </form>
    <p v-if="hasAppliedDates" class="mb-3 text-xs text-n-slate-11">
      {{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.APPLIED') }}:
      <span v-if="appliedSinceDate">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.SINCE_DATE') }}
        {{ appliedSinceDate }}
      </span>
      <span v-if="appliedUntilDate">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.FILTERS.UNTIL_DATE') }}
        {{ appliedUntilDate }}
      </span>
    </p>
    <p class="mb-4 text-xs text-n-slate-11">
      {{ t('REPORT.CAMPAIGN_PERFORMANCE.CREATION_DATE_NOTE') }}
    </p>
    <div
      class="flex flex-wrap justify-between gap-2 mb-4 text-xs text-n-slate-11"
    >
      <span>{{ t('REPORT.CAMPAIGN_PERFORMANCE.CACHE_NOTICE') }}</span>
      <span v-if="generatedAt">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.UPDATED_AT') }}:
        {{ formatUpdatedAt(generatedAt) }}
      </span>
    </div>
    <p
      v-if="hasError"
      role="alert"
      class="p-4 rounded-xl border border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ t('REPORT.CAMPAIGN_PERFORMANCE.ERROR') }}
    </p>
    <div
      v-else-if="isLoading"
      role="status"
      class="flex items-center justify-center py-24"
    >
      <Spinner />
    </div>
    <template v-else>
      <section
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <div
          class="flex flex-wrap justify-between gap-3 p-4 border-b border-n-weak"
        >
          <div>
            <h2 class="m-0 text-base font-semibold text-n-slate-12">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.TITLE') }}
            </h2>
            <p class="mt-1 mb-0 text-xs text-n-slate-11">
              {{ t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.HINT') }}
            </p>
          </div>
          <span class="text-sm tabular-nums text-n-slate-11">
            {{
              t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.COUNT', {
                count: formatNumber(total),
              })
            }}
          </span>
        </div>
        <div
          v-if="!campaigns.length"
          class="flex flex-col items-center gap-2 px-6 py-16 text-center"
        >
          <span
            class="i-lucide-megaphone size-8 text-n-slate-9"
            aria-hidden="true"
          />
          <strong class="text-sm text-n-slate-12">
            {{
              t(
                hasAppliedDates
                  ? 'REPORT.CAMPAIGN_PERFORMANCE.TABLE.NO_DATA_FILTERED'
                  : 'REPORT.CAMPAIGN_PERFORMANCE.TABLE.NO_DATA'
              )
            }}
          </strong>
          <p class="m-0 text-sm text-n-slate-11">
            {{
              t(
                hasAppliedDates
                  ? 'REPORT.CAMPAIGN_PERFORMANCE.TABLE.NO_DATA_FILTERED_HINT'
                  : 'REPORT.CAMPAIGN_PERFORMANCE.TABLE.NO_DATA_HINT'
              )
            }}
          </p>
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full min-w-[860px] text-sm text-left">
            <thead class="bg-n-alpha-black2 text-n-slate-11">
              <tr>
                <th scope="col" class="min-w-72 px-4 py-3 font-medium">
                  {{ t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.CAMPAIGN') }}
                </th>
                <th
                  v-for="metric in metrics"
                  :key="metric"
                  scope="col"
                  class="px-4 py-3 font-medium text-right whitespace-nowrap"
                >
                  {{ t(`REPORT.CAMPAIGN_PERFORMANCE.METRICS.${metric}`) }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="campaign in campaigns"
                :key="campaign.id"
                class="hover:bg-n-alpha-black2"
              >
                <th
                  scope="row"
                  class="px-4 py-4 font-normal align-top text-left"
                >
                  <button
                    type="button"
                    class="font-medium text-n-blue-11 hover:underline text-left"
                    :aria-label="
                      t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.OPEN_DETAIL', {
                        title: campaign.title,
                      })
                    "
                    @click="openDetail(campaign)"
                  >
                    {{ campaign.title }}
                  </button>
                  <div class="flex flex-wrap items-center gap-2 mt-2">
                    <span
                      class="inline-flex px-2 py-0.5 rounded-full text-xs"
                      :class="statusClass(campaign)"
                    >
                      {{ statusLabel(campaign) }}
                    </span>
                    <span class="text-xs text-n-slate-11">
                      {{ campaign.inbox || '—' }} ·
                      {{ campaign.template || '—' }}
                    </span>
                  </div>
                  <div
                    v-if="campaign.execution?.scheduled_at_local"
                    class="mt-2 text-xs text-n-slate-11"
                  >
                    {{ campaign.execution.scheduled_at_local }}
                    <span v-if="campaign.execution.scheduled_timezone"
                      >({{ campaign.execution.scheduled_timezone }})</span
                    >
                  </div>
                  <div class="mt-1 text-xs text-n-slate-11 tabular-nums">
                    {{
                      t('REPORT.CAMPAIGN_PERFORMANCE.METRICS.audience_total')
                    }}:
                    {{ formatNumber(campaign.audience_total) }}
                  </div>
                </th>
                <td
                  v-for="metric in metrics"
                  :key="metric"
                  class="px-4 py-4 text-right align-top tabular-nums text-n-slate-12"
                >
                  {{ formatNumber(campaign[metric]) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-if="total > perPage"
          class="flex items-center justify-between gap-3 px-4 py-3 border-t border-n-weak text-sm"
        >
          <button
            type="button"
            class="px-3 py-2 rounded-lg border border-n-weak disabled:opacity-50"
            :disabled="isLoading || page <= 1"
            @click="changePage(-1)"
          >
            {{ t('REPORT.CAMPAIGN_PERFORMANCE.PREVIOUS') }}
          </button>
          <span class="text-n-slate-11 tabular-nums">
            {{
              t('REPORT.CAMPAIGN_PERFORMANCE.TABLE.PAGE', {
                page,
                pages: pageCount,
              })
            }}
          </span>
          <button
            type="button"
            class="px-3 py-2 rounded-lg border border-n-weak disabled:opacity-50"
            :disabled="isLoading || page >= pageCount"
            @click="changePage(1)"
          >
            {{ t('REPORT.CAMPAIGN_PERFORMANCE.NEXT') }}
          </button>
        </div>
      </section>
      <p class="mt-4 text-xs text-n-slate-11">
        {{ t('REPORT.CAMPAIGN_PERFORMANCE.SEMANTICS') }}
      </p>
    </template>
    <WhatsAppCampaignReportDialog ref="dialog" :campaign="selected" />
  </div>
</template>
